import Box from '@mui/material/Box';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers';
import type { Dayjs } from 'dayjs';
import { createContext, useContext, useEffect } from 'react';
import type { DateRangeCalendarInternalValue } from './types';
import {
  type ReturnTypeUseDaySettings,
  useDaySettings,
} from './use-day-settings';

interface DayPickerContainerProps extends ReturnTypeUseDaySettings {
  children: React.ReactNode;
}

export interface DateRangeCalendarContextValue {
  pickerValue: DateRangeCalendarInternalValue;
  onRangeDaySelect: (event: React.MouseEvent, day: Dayjs) => void;
  lastSelectedDayIndex: number;
  rangePreviewDay: Dayjs | undefined;
  setRangePreviewDay: React.Dispatch<React.SetStateAction<Dayjs | undefined>>;
}

export const DateRangeCalendarContext =
  createContext<DateRangeCalendarContextValue | null>(null);

const DayPickerContainer = ({
  children,
  showSelectionCircle,
  isHighlightLeft,
  isHighlightRight,
  isFirstDayOfWeek,
  isLastDayOfWeek,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isStartOfPreviewRange,
  isEndOfPreviewRange,
  isInPreviewRange,
}: DayPickerContainerProps) => (
  <Box
    sx={{
      ...((!isHighlightLeft || isFirstDayOfWeek || isFirstDayOfMonth) && {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
      }),
      ...((!isHighlightRight || isLastDayOfWeek || isLastDayOfMonth) && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
      }),
      ...((isHighlightLeft || isHighlightRight) && {
        backgroundColor: 'primary.lighter',
      }),
      ...(showSelectionCircle &&
        isHighlightLeft && {
          mr: '1px',
        }),
      ...(showSelectionCircle &&
        isHighlightRight && {
          ml: '1px',
        }),
    }}
  >
    <Box
      sx={{
        borderWidth: '1px',
        px: '1px',
        borderStyle: 'dashed',
        borderColor: 'transparent',
        ...(isInPreviewRange && {
          borderTopColor: 'slate.400',
          borderBottomColor: 'slate.400',
        }),
        ...((isStartOfPreviewRange ||
          ((isFirstDayOfWeek || isFirstDayOfMonth) && isInPreviewRange)) && {
          borderTopLeftRadius: '50%',
          borderBottomLeftRadius: '50%',
          borderLeftColor: 'slate.400',
        }),
        ...((isEndOfPreviewRange ||
          ((isLastDayOfWeek || isLastDayOfMonth) && isInPreviewRange)) && {
          borderTopRightRadius: '50%',
          borderBottomRightRadius: '50%',
          borderRightColor: 'slate.400',
        }),
        ...(showSelectionCircle &&
          isHighlightLeft && {
            pr: 0,
          }),
        ...(showSelectionCircle &&
          isHighlightRight && {
            pl: 0,
          }),
      }}
    >
      {children}
    </Box>
  </Box>
);

export function RangePickerDay(props: PickersDayProps) {
  const ctx = useContext(DateRangeCalendarContext);

  if (!ctx) {
    return <PickersDay {...props} />;
  }

  return <RangePickerDayContent context={ctx} {...props} />;
}

function RangePickerDayContent({
  context,
  ...props
}: PickersDayProps & { context: DateRangeCalendarContextValue }) {
  const settings = useDaySettings({
    day: props.day,
    rangePreviewDay: context.rangePreviewDay,
    lastSelectedDayIndex: context.lastSelectedDayIndex,
    pickerValue: context.pickerValue,
    isOutsideMonth: props.outsideCurrentMonth,
  });

  useEffect(() => {
    if (props.autoFocus) {
      context.setRangePreviewDay(props.day);
    }
  }, [props.autoFocus]);

  return (
    <DayPickerContainer {...settings}>
      <PickersDay
        {...props}
        disableMargin
        onDaySelect={() => {}}
        onClick={(event) => {
          context.onRangeDaySelect(event, props.day);
        }}
        onMouseEnter={() => {
          context.setRangePreviewDay(props.day);
        }}
        selected={settings.showSelectionCircle}
        aria-selected={settings.isSelected}
      />
    </DayPickerContainer>
  );
}
